import * as jsBarcode from 'jsbarcode';
import { CardPrintConfig, CardPrintConfigExpirationStringType, ICard } from '@bciti/types';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { jsPDF } from 'jspdf';
import { Organisation, OrganisationConfiguration } from '../../global.model';
import { ShortDatePipe } from '../../pipes/shortDate/short-date.pipe';
import { GetUserActiveSubscriptionsResponse } from '../../services/config-all/config-all.model';
import { MonserratFont } from '../fonts/MontserratFont';

@Injectable({
    providedIn: 'root',
})
export class CardPrintService {
    constructor(private sanitizer: DomSanitizer) {}

    public async generateBarCodePdf(
        card: ICard | any,
        org: Organisation,
        orgConfig: any,
        subscriptions: GetUserActiveSubscriptionsResponse[],
        translate: TranslateService,
        template: CardPrintConfig,
        sanitize: boolean,
        shortDatePipe: ShortDatePipe,
    ): Promise<{
        url: SafeResourceUrl | string;
        output: any;
    }> {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [85.852, 54.102],
        });

        // 3.38 in = 85.852 mm
        const ratioX = 85.852 / 145;
        // 2.13 in = 54.102 mm
        const ratioY = 54.102 / 96;

        // outline Cutting Help
        if (template.outlineCuttingHelp) {
            doc.setLineDashPattern([3, 3], 0);
            doc.setLineWidth(1);
            doc.line(0, 0, 145 * ratioX, 0);
            doc.line(145 * ratioX, 0, 145 * ratioX, 96 * ratioY);
            doc.line(145 * ratioX, 96 * ratioY, 0, 96 * ratioY);
            doc.line(0, 96 * ratioY, 0, 0);
        }

        doc.setLineDashPattern([1, 0], 0);

        const getNewValueX = (oldVal: number) => {
            return oldVal * ratioX;
        };

        const getNewValueY = (oldVal: number) => {
            return oldVal * ratioY;
        };

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        const pageHeightSpacer = pageHeight * 0.2;

        const x = getNewValueX(60);

        // cardType
        if (template.cardType.display) {
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(this.getCardLabel(orgConfig, card, translate), getNewValueX(template.cardType.x), getNewValueY(template.cardType.y), { align: 'center' });
        }

        // photo
        if (template.photo.display) {
            const imgBase64 = await this.getBase64ImageFromUrl(card.photo);
            doc.addImage(`${imgBase64}`, card.photo.split('.').pop(), getNewValueX(template.photo.x), getNewValueY(template.photo.y), template.photo.width, template.photo.height);
        }

        // // org logo
        if (template?.orgLogo?.display) {
            const imgBase64 = await this.getBase64ImageFromUrl(org.logo);
            doc.addImage(`${imgBase64}`, org.logo.split('.').pop(), getNewValueX(template.orgLogo.x), getNewValueY(template.orgLogo.y), template.orgLogo.width, template.orgLogo.height);
        }

        // bciti+ logo
        if (template?.bcitiLogo?.display) {
            const imgBase64 = await this.getBase64ImageFromUrl('/assets/images/bciti-logo.png');
            doc.addImage(`${imgBase64}`, 'png', getNewValueX(template.bcitiLogo.x), getNewValueY(template.bcitiLogo.y), template.bcitiLogo.width, template.bcitiLogo.height);
        }

        // residency
        if (template.residency.display || (!template.residency.display && card.subType !== 'RESIDENT')) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.text(this.subTypeName(orgConfig, card, translate).toUpperCase(), getNewValueX(template.residency.x), getNewValueY(template.residency.y), {
                align: 'center',
            });
        }

        // first name, last name
        if (template.name.display) {
            // Ensure names are strings to avoid potential errors.
            const firstName = card.firstName || '';
            const lastName = card.lastName || '';
            const maxLength = Math.max(firstName.length, lastName.length);

            // Determine the adjusted font size using a ternary chain.
            const adjustedFontSize = maxLength > 25 ? 6 : maxLength > 20 ? 8.5 : maxLength > 15 ? 10 : maxLength > 10 ? 11 : template.name.fontSize || 12;

            doc.setFontSize(adjustedFontSize);
            doc.setFont('helvetica', 'bold');
            doc.text(`${firstName}\n${lastName}`, getNewValueX(template.name.x), getNewValueY(template.name.y), { maxWidth: template.name.maxWidth });
        }

        // address
        if (template.address.display) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(`${card.address?.unit ? card.address?.unit + '-' : ''}${card.address?.number}, ${card.address?.street}`, getNewValueX(template.address.x), getNewValueY(template.address.y));
            doc.text(`${card.address?.city}, ${card.address?.province}`, getNewValueX(template.address.x), getNewValueY(6 + template.address.y));
            doc.text(`${card.address.postCode}`, getNewValueX(template.address.x), getNewValueY(12 + template.address.y));
        }

        // barcode
        if (template.scanCode.display) {
            // Create an SVG element
            const barcodeSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

            // Generate the barcode in the SVG element
            jsBarcode(barcodeSvg, card.code, {
                format: 'CODE39',
                width: getNewValueX(template.scanCode.width),
                height: getNewValueY(template.scanCode.height),
                font: 'Sans-serif',
                fontSize: 24,
                textMargin: 1,
                margin: 0,
                displayValue: false,
            });

            // Convert the SVG element to a base64 image
            const barcodeBase64 = await this.getBase64ImageFromSvg(barcodeSvg);

            // Add the base64 image to the PDF
            doc.addImage(barcodeBase64, 'PNG', getNewValueX(template.scanCode.x), getNewValueY(template.scanCode.y), getNewValueX(template.scanCode.width), getNewValueY(template.scanCode.height));
        }

        // cardcode
        if (template.cardCode.display) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(`${card.code}`, getNewValueX(template.cardCode.x), getNewValueY(template.cardCode.y), { align: 'center' });
        }

        // Expiration date
        // Expiration date
        if (card.expirationDate && template.expiration.rectangle.display === true) {
            doc.setFontSize(6);
            doc.setFont('helvetica', 'normal');
            doc.setDrawColor(255, 255, 255);
            doc.setFillColor(0, 0, 0);
            doc.rect(
                getNewValueX(template.expiration.rectangle.x),
                getNewValueY(template.expiration.rectangle.y),
                getNewValueX(template.expiration.rectangle.width),
                getNewValueY(template.expiration.rectangle.height),
                'FD',
            );
            doc.setTextColor('white');
            doc.text(`${this.formatDate(card.expirationDate.toString(), translate, template.expiration.stringType)}`, getNewValueX(template.expiration.x), getNewValueY(template.expiration.y), {
                align: 'center',
            });
        }

        // Subscriptions second page
        const subscriptionsList = this.getSubscriptionsOrderedList(subscriptions, translate);
        if (subscriptionsList.length) {
            doc.addFileToVFS('Montserrat Medium 500-normal.ttf', MonserratFont);
            doc.addFont('Montserrat Medium 500-normal.ttf', 'Montserrat Medium 500', 'normal');
            doc.setFont('Montserrat Medium 500');
            doc.insertPage(2);
            doc.setFontSize(8);
            doc.setDrawColor('#8792A5'); // $bciti-gray-medium

            // Define a line height (in mm) for vertical spacing. Adjust as needed.
            const lineHeight = 2.5;
            let Yposition = 8;

            for (let i = 0; i < subscriptionsList.length; i++) {
                const subscription = subscriptionsList[i];

                // Combine subscription name and type with a separator.
                const combinedText = subscription.name + ' | ' + subscription.subscriptionType;
                // Split the combined text to fit within a maxWidth of 45 mm.
                let combinedLines = doc.splitTextToSize(combinedText, 45);
                // Limit to at most 2 lines if it overflows.
                if (combinedLines.length > 2) {
                    combinedLines = combinedLines.slice(0, 2);
                }

                // Print the combined text (name and type) on the left.
                doc.setTextColor('#363E41'); // $bciti-dark-gray
                doc.text(combinedLines, 6, Yposition, { maxWidth: 45 });

                // Determine the total block height occupied by the combined text.
                const combinedBlockHeight = combinedLines.length * lineHeight;

                // For the expiration date on the right, use a vertical offset:
                // If the combined text fits on one line, print at Yposition + 1.5.
                // Otherwise (if two lines), print below the block.
                const expirationY = combinedLines.length === 1 ? Yposition + 1.5 : Yposition + combinedBlockHeight + 1.5;
                doc.setTextColor('#8792A5'); // $bciti-gray-medium
                const formattedDate = shortDatePipe.transform(subscription.expirationDate);
                doc.text('Exp. ' + formattedDate, 78.5, expirationY, { align: 'right' });

                // Now print the subscription code on the line immediately after the combined text block.
                const codeY = combinedLines.length === 1 ? Yposition + 3 : Yposition + combinedBlockHeight + lineHeight;
                doc.setFontSize(6);
                doc.setTextColor('#363E41');
                doc.text(subscription.code, 6, codeY, { maxWidth: 45 });
                doc.setFontSize(8);

                // Optionally, draw a separator line if this is not the last subscription.
                if (i !== subscriptionsList.length - 1) {
                    const separatorY = codeY + 1; // adjust as needed
                    doc.line(4, separatorY, 82, separatorY);
                }

                // Update Yposition for the next subscription.
                // We add an extra margin (e.g. lineHeight + 1.5) to keep spacing between entries.
                Yposition = codeY + lineHeight + 1.5;
            }
        }

        const url = window.URL.createObjectURL(new Blob([doc.output('arraybuffer')], { type: 'application/pdf' }));

        let responseUrl;
        let output;

        if (navigator.userAgent.includes('Chrome')) {
            output = doc.output('datauristring');
        } else {
            output = doc.output('datauri');
        }

        if (sanitize) {
            responseUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        } else {
            responseUrl = url;
        }

        return { url: responseUrl, output };
    }

    public async generateQrCodePdf(
        card: ICard | any,
        org: Organisation,
        orgConfig: any,
        subscriptions: GetUserActiveSubscriptionsResponse[],
        translate: TranslateService,
        template: CardPrintConfig,
        sanitize: boolean,
        shortDatePipe: ShortDatePipe,
    ): Promise<{
        url: SafeResourceUrl | string;
        output: any;
    }> {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [85.852, 54.102],
        });

        // 3.38 in = 85.852 mm
        const ratioX = 85.852 / 145;
        // 2.13 in = 54.102 mm
        const ratioY = 54.102 / 96;

        const getNewValueX = (oldVal: number) => {
            return oldVal * ratioX;
        };

        const getNewValueY = (oldVal: number) => {
            return oldVal * ratioY;
        };

        const x = getNewValueX(60);

        // outline Cutting Help
        if (template.outlineCuttingHelp) {
            doc.setLineDashPattern([3, 3], 0);
            doc.setLineWidth(1);
            doc.line(0, 0, 145 * ratioX, 0);
            doc.line(145 * ratioX, 0, 145 * ratioX, 96 * ratioY);
            doc.line(145 * ratioX, 96 * ratioY, 0, 96 * ratioY);
            doc.line(0, 96 * ratioY, 0, 0);
        }

        doc.setLineDashPattern([1, 0], 0);

        // cardType
        if (template.cardType.display) {
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(this.getCardLabel(orgConfig, card, translate), getNewValueX(template.cardType.x), getNewValueY(template.cardType.y), { align: 'center' });
        }

        // residency
        if (template.residency.display || (!template.residency.display && card.subType !== 'RESIDENT')) {
            doc.setFontSize(5);
            doc.setFont('helvetica', 'normal');
            doc.text(this.subTypeName(orgConfig, card, translate).toUpperCase(), getNewValueX(template.residency.x), getNewValueY(template.residency.y), {
                align: 'center',
            });
        }

        // photo
        if (template.photo.display) {
            const imgBase64 = await this.getBase64ImageFromUrl(card.photo);
            doc.addImage(`${imgBase64}`, card.photo.split('.').pop(), getNewValueX(template.photo.x), getNewValueY(template.photo.y), template.photo.width, template.photo.height);
        }

        // // org logo
        if (template?.orgLogo?.display) {
            const imgBase64 = await this.getBase64ImageFromUrl(org.logo);
            doc.addImage(`${imgBase64}`, org.logo.split('.').pop(), getNewValueX(template.orgLogo.x), getNewValueY(template.orgLogo.y), template.orgLogo.width, template.orgLogo.height);
        }

        // bciti+ logo
        if (template?.bcitiLogo?.display) {
            const imgBase64 = await this.getBase64ImageFromUrl('/assets/images/bciti-logo.png');
            doc.addImage(`${imgBase64}`, 'png', getNewValueX(template.bcitiLogo.x), getNewValueY(template.bcitiLogo.y), template.bcitiLogo.width, template.bcitiLogo.height);
        }

        // first name, last name
        if (template.name.display) {
            doc.setFontSize(template.name.fontSize || 8);
            doc.setFont('helvetica', 'bold');
            doc.text(`${card?.firstName} ${card?.lastName}`, getNewValueX(template.name.x), getNewValueY(template.name.y), {
                align: 'center',
                maxWidth: template.name.maxWidth,
            });
        }

        // address
        if (template.address.display) {
            doc.setFontSize(6);
            doc.setFont('helvetica', 'normal');
            doc.text(`${card?.address?.unit ? card?.address?.unit + '-' : ''}${card?.address?.number}, ${card?.address?.street}`, getNewValueX(template.address.x), getNewValueY(template.address.y), {
                align: 'center',
            });
            doc.text(`${card?.address?.city}, ${card?.address?.province}, ${card.address.postCode}`, getNewValueX(template.address.x), getNewValueY(template.address.y + 5), {
                align: 'center',
            });
        }

        // qrcode
        if (template.scanCode.display) {
            const qrcode = document.getElementById('qrcode');
            doc.addImage(
                this.getBase64Image(qrcode.firstChild.firstChild),
                'JPEG',
                getNewValueX(template.scanCode.x),
                getNewValueY(template.scanCode.y),
                template.scanCode.width,
                template.scanCode.height,
            );
        }

        // card code
        if (template.cardCode.display) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(`${card.code}`, getNewValueX(template.cardCode.x), getNewValueY(template.cardCode.y), {
                align: 'center',
            });
        }

        // Expiration date
        if (card.expirationDate && template.expiration.rectangle.display === true) {
            doc.setFontSize(6);
            doc.setFont('helvetica', 'normal');
            doc.setDrawColor(255, 255, 255);
            doc.setFillColor(0, 0, 0);
            doc.rect(
                getNewValueX(template.expiration.rectangle.x),
                getNewValueY(template.expiration.rectangle.y),
                getNewValueX(template.expiration.rectangle.width),
                getNewValueY(template.expiration.rectangle.height),
                'FD',
            );
            doc.setTextColor('white');
            doc.text(`${this.formatDate(card.expirationDate.toString(), translate, template.expiration.stringType)}`, getNewValueX(template.expiration.x), getNewValueY(template.expiration.y), {
                align: 'center',
            });
        }

        // Subscriptions second page
        const subscriptionsList = this.getSubscriptionsOrderedList(subscriptions, translate);
        if (subscriptionsList.length) {
            doc.addFileToVFS('Montserrat Medium 500-normal.ttf', MonserratFont);
            doc.addFont('Montserrat Medium 500-normal.ttf', 'Montserrat Medium 500', 'normal');
            doc.setFont('Montserrat Medium 500');
            doc.insertPage(2);
            doc.setFontSize(8);
            doc.setDrawColor('#8792A5'); // $bciti-gray-medium

            // Define a line height (in mm) for vertical spacing. Adjust as needed.
            const lineHeight = 2.5;
            let Yposition = 8;

            for (let i = 0; i < subscriptionsList.length; i++) {
                const subscription = subscriptionsList[i];

                // Combine subscription name and type with a separator.
                const combinedText = subscription.name + ' | ' + subscription.subscriptionType;
                // Split the combined text to fit within a maxWidth of 45 mm.
                let combinedLines = doc.splitTextToSize(combinedText, 45);
                // Limit to at most 2 lines if it overflows.
                if (combinedLines.length > 2) {
                    combinedLines = combinedLines.slice(0, 2);
                }

                // Print the combined text (name and type) on the left.
                doc.setTextColor('#363E41'); // $bciti-dark-gray
                doc.text(combinedLines, 6, Yposition, { maxWidth: 45 });

                // Determine the total block height occupied by the combined text.
                const combinedBlockHeight = combinedLines.length * lineHeight;

                // For the expiration date on the right, use a vertical offset:
                // If the combined text fits on one line, print at Yposition + 1.5.
                // Otherwise (if two lines), print below the block.
                const expirationY = combinedLines.length === 1 ? Yposition + 1.5 : Yposition + combinedBlockHeight + 1.5;
                doc.setTextColor('#8792A5'); // $bciti-gray-medium
                const formattedDate = shortDatePipe.transform(subscription.expirationDate);
                doc.text('Exp. ' + formattedDate, 78.5, expirationY, { align: 'right' });

                // Now print the subscription code on the line immediately after the combined text block.
                const codeY = combinedLines.length === 1 ? Yposition + 3 : Yposition + combinedBlockHeight + lineHeight;
                doc.setFontSize(6);
                doc.setTextColor('#363E41');
                doc.text(subscription.code, 6, codeY, { maxWidth: 45 });
                doc.setFontSize(8);

                // Optionally, draw a separator line if this is not the last subscription.
                if (i !== subscriptionsList.length - 1) {
                    const separatorY = codeY + 1; // adjust as needed
                    doc.line(4, separatorY, 82, separatorY);
                }

                // Update Yposition for the next subscription.
                // We add an extra margin (e.g. lineHeight + 1.5) to keep spacing between entries.
                Yposition = codeY + lineHeight + 1.5;
            }
        }

        const url = window.URL.createObjectURL(new Blob([doc.output('arraybuffer')], { type: 'application/pdf' }));

        let responseUrl;
        let output;

        if (navigator.userAgent.includes('Chrome')) {
            output = doc.output('datauristring');
        } else {
            output = doc.output('datauri');
        }

        if (sanitize) {
            responseUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        } else {
            responseUrl = url;
        }

        return { url: responseUrl, output };
    }

    private subTypeName(orgConfig: any, card: ICard, translate: TranslateService) {
        return orgConfig?.subTypes?.find((c) => c.tag === card.subType)?.name[translate.currentLang] || '';
    }

    public getCardLabel(orgConfig: OrganisationConfiguration, card: ICard | any, translate: TranslateService) {
        const cardType = orgConfig?.support?.cards?.find((i) => i.type === card.cardType);

        return cardType?.identity?.label[translate.currentLang] || '';
    }

    private getSubscriptionsOrderedList(subscriptions: GetUserActiveSubscriptionsResponse[] = [], translate: TranslateService) {
        const activeSubscriptions = subscriptions.filter((s) => new Date(s.endDate).getTime() > new Date().getTime());
        return activeSubscriptions
            .map((sub) => ({
                expirationDate: sub.endDate,
                name: sub.name,
                code: sub.code,
                subscriptionType: this.getSubscriptionTypeText(sub.subscriptionType, translate),
            }))
            .sort((a, b) => new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime())
            .slice(0, 5);
    }

    private formatDate(
        date: string,
        translate: TranslateService,
        stringType: CardPrintConfigExpirationStringType,
        options: Intl.DateTimeFormatOptions = {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        },
    ) {
        switch (stringType) {
            case CardPrintConfigExpirationStringType.SHORT:
                return `Exp: ${new Intl.DateTimeFormat(`${translate.currentLang}-CA`, options).format(new Date(date))}`;
            case CardPrintConfigExpirationStringType.NUMERIC:
            default:
                return `Exp: ${new Intl.DateTimeFormat(`${translate.currentLang}-CA`).format(new Date(date))}`;
        }
    }

    private async getBase64ImageFromUrl(imageUrl: string) {
        const res = await fetch(imageUrl);
        const blob = await res.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.addEventListener(
                'load',
                () => {
                    resolve(reader.result);
                },
                false,
            );

            reader.onerror = () => {
                return reject(this);
            };
            reader.readAsDataURL(blob);
        });
    }

    private async getBase64ImageFromSvg(svgElement: SVGElement): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const svgString = new XMLSerializer().serializeToString(svgElement);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.onload = function () {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = function () {
                reject(new Error('Failed to load SVG image'));
            };
            img.src = `data:image/svg+xml;base64,${btoa(svgString)}`;
        });
    }

    private getBase64Image(img) {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        return dataURL;
    }

    private getSubscriptionTypeText(subscriptionType: string, translate: TranslateService) {
        switch (subscriptionType) {
            case 'FAMILY':
                return translate.instant('pages.user-cards.subscription.type.family');
            default:
                return translate.instant('pages.user-cards.subscription.type.individual');
        }
    }
}
